import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Button,
  Stack,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";

/**
 * Dialog per la raccolta dati del questionario preventivo.
 * I campi noti vengono precompilati; l'utente può completare e inviare.
 */
export default function PreventivoDialog({
  open,
  onClose,
  onSubmit,
  copertura,
  knownData,
}) {
  const initialValues = useMemo(
    () => ({
      ragioneSociale: knownData?.ragioneSociale || "",
      partitaIVA: knownData?.partitaIVA || "",
      settore: knownData?.settore || "",
      fatturato: knownData?.fatturato || "",
      attivitaConsulenza: "",
      clientiOver1M: "",
      danniPatrimoniali: "",
      rcAttiva: "",
      sinistri3anni: "",
      massimale: "",
      retroattivita: "",
      note: "",
    }),
    [knownData]
  );

  const [values, setValues] = useState(initialValues);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit?.({ ...values, copertura });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Questionario Preventivo - {copertura?.tipo || "Copertura"}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compila i dati richiesti per generare il preventivo. I campi noti sono
          stati precompilati.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ragione Sociale"
              fullWidth
              value={values.ragioneSociale}
              onChange={handleChange("ragioneSociale")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Partita IVA / Codice Fiscale"
              fullWidth
              value={values.partitaIVA}
              onChange={handleChange("partitaIVA")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Settore di Attività"
              fullWidth
              value={values.settore}
              onChange={handleChange("settore")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fatturato Annuale (€)"
              type="number"
              fullWidth
              value={values.fatturato}
              onChange={handleChange("fatturato")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Attività di consulenza/progettazione?
            </Typography>
            <RadioGroup
              row
              value={values.attivitaConsulenza}
              onChange={handleChange("attivitaConsulenza")}
            >
              <FormControlLabel value="si" control={<Radio />} label="Sì" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Clienti con fatturato &gt; €1M?
            </Typography>
            <RadioGroup
              row
              value={values.clientiOver1M}
              onChange={handleChange("clientiOver1M")}
            >
              <FormControlLabel value="si" control={<Radio />} label="Sì" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Danni potenzialmente solo patrimoniali?
            </Typography>
            <RadioGroup
              row
              value={values.danniPatrimoniali}
              onChange={handleChange("danniPatrimoniali")}
            >
              <FormControlLabel value="si" control={<Radio />} label="Sì" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Copertura RC Professionale attualmente attiva?
            </Typography>
            <RadioGroup
              row
              value={values.rcAttiva}
              onChange={handleChange("rcAttiva")}
            >
              <FormControlLabel value="si" control={<Radio />} label="Sì" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Sinistri negli ultimi 3 anni?
            </Typography>
            <RadioGroup
              row
              value={values.sinistri3anni}
              onChange={handleChange("sinistri3anni")}
            >
              <FormControlLabel value="si" control={<Radio />} label="Sì" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Massimale Richiesto"
              fullWidth
              value={values.massimale}
              onChange={handleChange("massimale")}
            >
              <MenuItem value="">Seleziona...</MenuItem>
              <MenuItem value="500000">€ 500.000</MenuItem>
              <MenuItem value="1000000">€ 1.000.000</MenuItem>
              <MenuItem value="2000000">€ 2.000.000</MenuItem>
              <MenuItem value="5000000">€ 5.000.000</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Retroattività Richiesta"
              fullWidth
              value={values.retroattivita}
              onChange={handleChange("retroattivita")}
            >
              <MenuItem value="">Seleziona...</MenuItem>
              <MenuItem value="nessuna">Nessuna</MenuItem>
              <MenuItem value="1anno">1 anno</MenuItem>
              <MenuItem value="2anni">2 anni</MenuItem>
              <MenuItem value="3anni">3 anni</MenuItem>
              <MenuItem value="5anni">5 anni</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Note Aggiuntive"
              fullWidth
              multiline
              minRows={3}
              value={values.note}
              onChange={handleChange("note")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="text">
          Annulla
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          Completa Questionario
        </Button>
      </DialogActions>
    </Dialog>
  );
}
